import { useContext, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { createOptions } from "@/modules/common/utils";
import { Options } from "@/modules/common/types";
import useLocationsData from "@/modules/common/hooks/locations-data.hook";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import { SalesResponseV2 } from "@/modules/sales/dtos";
import { SaleDocumentType } from "@/modules/leads/constants";

const dateSchema = z
  .date({
    required_error: "Required",
    invalid_type_error: "Invalid date",
  })
  .nullable();

const saleFormSchema = z
  .object({
    unitValue: z.string().transform((val, ctx) => {
      const parsed = parseFloat(val);
      if (isNaN(parsed)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Not a number",
        });
        return z.NEVER;
      }
      return parsed;
    }),
    unitNumber: z.string().min(1, "Required"),
    unitArea: z.string().transform((val, ctx) => {
      const parsed = parseFloat(val);
      if (isNaN(parsed)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Not a number",
        });
        return z.NEVER;
      }
      if (parsed < 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Must be a positive number",
        });
        return z.NEVER;
      }
      return parsed;
    }),
    finishing: z.string().min(1, "Required"),
    areaUId: z.string().optional(),
    compoundUId: z.string().min(1, "Required"),
    downPayment: z.string().transform((val, ctx) => {
      const parsed = parseFloat(val);
      if (isNaN(parsed)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Not a number",
        });
        return z.NEVER;
      }
      return parsed;
    }),
    propertyTypeUId: z.string().min(1, "Required"),
    dateOfReservation: dateSchema,
    dateOfContraction: dateSchema,
    actualDateOfContraction: dateSchema,
    dateOfDownPayment: dateSchema,
    yearsOfPayment: z.string().transform((val, ctx) => {
      const parsed = parseInt(val, 10);
      if (isNaN(parsed)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Not a number",
        });
        return z.NEVER;
      }
      return parsed;
    }),
    saleCategory: z.string().min(1, "Required"),
    nameOnContract: z.string().min(1, "Required"),
    cashPrice: z
      .string()
      .optional()
      .transform((val, ctx) => {
        if (!val) return null;
        const parsed = parseFloat(val);
        if (isNaN(parsed)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Not a number",
          });
          return z.NEVER;
        }
        return parsed;
      }),
    comment: z.string().min(4, "Minimum 4 characters"),
    organizationUId: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.dateOfReservation) {
        if (
          data.dateOfContraction &&
          data.dateOfReservation > data.dateOfContraction
        ) {
          return false;
        }
        if (
          data.actualDateOfContraction &&
          data.dateOfReservation > data.actualDateOfContraction
        ) {
          return false;
        }
      }
      return true;
    },
    {
      message:
        "Date of reservation cannot be after date of contraction or actual date of contraction",
      path: ["dateOfReservation"],
    }
  );

type SaleFormData = z.infer<typeof saleFormSchema>;

const useSaleForm = ({
  selectedUnitLocation,
  handleSaleSubmission,
  existingSale,
}: {
  selectedUnitLocation: Options | undefined;
  handleSaleSubmission: (
    formattedData: any,
    formType: "sale" | "form",
    editedSaleData?: {
      saleUId: string;
      leadUId: string;
    }
  ) => void;
  existingSale?: SalesResponseV2;
}) => {
  const { lead } = useContext(LeadPageContext);

  const { PropertyTypeData } = useLocationsData();

  const [propertyTypeOptions, setPropertyTypeOptions] = useState<any[]>([]);
  const [saleDocumentTypeOption, setSaleDocumentTypeOption] = useState<
    SaleDocumentType | undefined
  >(undefined);

  const formDefaultValues = useMemo(() => {
    return {
      unitValue: existingSale?.unitValue ?? undefined,
      unitNumber: existingSale?.unitNumber ?? undefined,
      unitArea: existingSale?.unitArea ?? undefined,
      finishing: existingSale?.finishing ?? "",
      areaUId: existingSale?.area?.uid ?? undefined,
      compoundUId: existingSale?.compound?.uid ?? undefined,
      downPayment: existingSale?.downPayment ?? undefined,
      propertyTypeUId: existingSale?.propertyType?.uid ?? undefined,
      ...(existingSale?.dateOfReservation && {
        dateOfReservation: new Date(existingSale.dateOfReservation),
      }),
      dateOfContraction: existingSale?.dateOfContraction
        ? new Date(existingSale.dateOfContraction)
        : new Date(),
      ...(existingSale?.actualDateOfContraction && {
        actualDateOfContraction: new Date(existingSale.actualDateOfContraction),
      }),

      ...(existingSale?.dateOfDownPayment && {
        dateOfDownPayment: new Date(existingSale.dateOfDownPayment),
      }),
      yearsOfPayment: existingSale?.yearsOfPayment ?? undefined,
      saleCategory: existingSale?.saleCategory ?? "",
      nameOnContract: existingSale?.nameOnContract ?? "",
      cashPrice: existingSale?.cashPrice ?? undefined,
      comment: existingSale?.comment ?? "",
      organizationUId: existingSale?.organization?.uid ?? undefined,
    };
  }, [existingSale]);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<SaleFormData>({
    resolver: zodResolver(saleFormSchema),
    defaultValues: formDefaultValues,
  });

  const selectedSaleCategory = watch("saleCategory");
  const selectedCompound = watch("compoundUId");
  const selectedPropertyType = watch("propertyTypeUId");

  useEffect(() => {
    setPropertyTypeOptions(
      createOptions({
        list: PropertyTypeData,
        field1: "uid",
        field2: "name",
      })
    );
  }, [PropertyTypeData]);

  useEffect(() => {
    if (existingSale) {
      reset({
        unitValue: existingSale.unitValue ?? undefined,
        unitNumber: existingSale.unitNumber ?? "",
        unitArea: existingSale.unitArea ?? undefined,
        finishing: existingSale.finishing ?? "",
        areaUId: existingSale?.area?.uid ?? "",
        compoundUId: existingSale?.compound?.uid ?? "",
        downPayment: existingSale.downPayment ?? undefined,
        propertyTypeUId: existingSale?.propertyType?.uid ?? "",
        ...(existingSale?.dateOfReservation && {
          dateOfReservation: new Date(existingSale.dateOfReservation),
        }),
        dateOfContraction: existingSale.dateOfContraction
          ? new Date(existingSale.dateOfContraction)
          : new Date(),
        ...(existingSale?.actualDateOfContraction && {
          actualDateOfContraction: new Date(
            existingSale.actualDateOfContraction
          ),
        }),

        ...(existingSale?.dateOfDownPayment && {
          dateOfDownPayment: new Date(existingSale.dateOfDownPayment),
        }),
        yearsOfPayment: existingSale.yearsOfPayment ?? undefined,
        saleCategory: existingSale.saleCategory ?? "",
        nameOnContract: existingSale.nameOnContract ?? "",
        cashPrice: existingSale.cashPrice ?? undefined,
        comment: existingSale.comment ?? "",
        organizationUId: existingSale.organization?.uid ?? undefined,
      });
    }
  }, [existingSale, reset]);

  const formatDate = (date: Date | null): string | null => {
    if (!date) return null;
    return date.toISOString().split("T")[0];
  };

  const onSubmit = (data: SaleFormData) => {
    const cashPrice =
      lead?.data?.lead_type?.lead_type === "Nawy Now" ? data.cashPrice : null;

    const formattedData = {
      actualDateOfContraction: formatDate(data.actualDateOfContraction),
      areaUId: existingSale?.area?.uid || selectedUnitLocation?.optionValueUid,
      ...(data.cashPrice && { cashPrice: data.cashPrice }),
      compoundUId: data.compoundUId,
      dateOfContraction: formatDate(data.dateOfContraction),
      ...(data.dateOfDownPayment && {
        dateOfDownPayment: formatDate(data.dateOfDownPayment),
      }),
      ...(data.dateOfReservation && {
        dateOfReservation: formatDate(data.dateOfReservation),
      }),
      downPayment: data.downPayment,
      nameOnContract: data.nameOnContract,
      propertyTypeUId: data.propertyTypeUId,
      saleCategory: data.saleCategory,
      saleTypeName: "Contracted",
      ...(selectedUnitLocation && { unitLocation: selectedUnitLocation.label }),
      unitArea: data.unitArea,
      finishing: data.finishing,
      unitNumber: data.unitNumber,
      unitValue: data.unitValue,
      ...(!existingSale && { journeyUId: lead?.data?.lead?.journey_uid }),
      ...(!existingSale && { userUId: lead?.data?.lead?.user?.id }),
      ...(!existingSale && {
        organizationUId: lead?.data?.lead?.organization?.uid,
      }),
      ...(cashPrice && { cashPrice: cashPrice }),
      yearsOfPayment: data.yearsOfPayment,
      comment: data.comment,
    };

    if (existingSale) {
      handleSaleSubmission(formattedData, "sale", {
        saleUId: existingSale?.uid,
        leadUId: existingSale?.lead?.uid || "",
      });
    } else {
      handleSaleSubmission(formattedData, "sale");
    }
  };

  return {
    control,
    handleSubmit,
    errors,
    onSubmit,
    selectedSaleCategory,
    propertyTypeOptions,
    selectedCompound,
    selectedPropertyType,
    saleDocumentTypeOption,
    setSaleDocumentTypeOption,
  };
};

export default useSaleForm;
